import moment from 'moment';

import { DISPLAY_TIME_FORMAT } from './formatDisplayDateTime';
import {
  formatMoney,
  formatNumberDecimal,
  getCurrencyDisplayFormat,
} from './libs';
import type { StkRequestSnapshot } from './mpesaStkPrompt';
import { notificationDayLabel } from './notificationCard';

/**
 * Owner-side "Payments to check": the M-Pesa prompts the server lists under
 * `bp_get_mpesa_stk_requests_needing_attention_information` (status
 * `unknown` / `needs_review`, or flagged), with the context the owner needs to
 * settle them from the M-Pesa statement. Pure functions only; the screens and
 * the slice read everything through these.
 */

// M-Pesa money is always Kenyan shillings, whatever the location's currency.
export const MPESA_CURRENCY = getCurrencyDisplayFormat('KES');

export interface PaymentToCheckOrderItem {
  name: string;
  quantity: number | null;
  mass: number | null;
  unit_of_mass: string | null;
  line_total: number;
  /** Marked paid on the order. */
  paid: boolean;
  /** This prompt's money is for this line (false on a partial checkout's other lines). */
  pays: boolean;
}

export interface PaymentToCheckOrder {
  id: string;
  receipt_ref: string | null;
  /** Null for a walk-in order. */
  customer_name: string | null;
  customer_id: string | null;
  status: string | null;
  created_by: string | null;
  amount_total: number;
  amount_paid: number;
  amount_due: number;
  item_count: number;
  /** Lines a partial-checkout prompt pays for; null when it pays everything owed. */
  pays_item_count: number | null;
  items: PaymentToCheckOrderItem[];
}

export interface PaymentToCheckPayer {
  /** The order's customer, if it has one. */
  name: string | null;
  /** "0712 •••678", or empty when unknown. */
  phone: string;
}

export interface PaymentToCheckPaidTo {
  type: 'till' | 'paybill';
  number: string;
}

/** One prompt as the owner endpoints describe it. */
export interface PaymentToCheck extends StkRequestSnapshot {
  bp_payer?: PaymentToCheckPayer | null;
  bp_sent_by?: string | null;
  bp_paid_to?: PaymentToCheckPaidTo | null;
  bp_order?: PaymentToCheckOrder | null;
}

export type PaymentToCheckTone = 'due' | 'info' | 'success' | 'neutral';

/** The filter buckets; a flagged prompt counts under its status too. */
export type PaymentToCheckBucket = 'unconfirmed' | 'not_applied' | 'flagged';
export type PaymentToCheckFilter = 'all' | PaymentToCheckBucket;

export interface PaymentToCheckDescription {
  /** The bucket its status puts it in; `flagged` only when nothing is unresolved. */
  bucket: PaymentToCheckBucket;
  statusLabel: string;
  tone: PaymentToCheckTone;
  /** What happened, in the owner's words. */
  explanation: string;
  flagLabel: string | null;
  flagExplanation: string | null;
  /** Owner settles it from the statement: Record payment / Not paid. */
  canResolve: boolean;
  /** Owner has looked at a flagged payment: Mark as checked. */
  canClearFlag: boolean;
}

const UNRESOLVED_STATUSES: ReadonlySet<string> = new Set([
  'unknown',
  'needs_review',
]);

const STATUS_COPY: Record<
  string,
  { label: string; tone: PaymentToCheckTone; explanation: string }
> = {
  unknown: {
    label: 'Not confirmed',
    tone: 'due',
    explanation:
      'Safaricom did not confirm this prompt. Check your M-Pesa statement for it.',
  },
  needs_review: {
    label: 'Received, not applied',
    tone: 'info',
    explanation:
      'M-Pesa confirmed this payment. BizPlus could not add it to the order.',
  },
  paid: {
    label: 'Paid',
    tone: 'success',
    explanation: 'The money is on the order.',
  },
  failed: {
    label: 'Not paid',
    tone: 'neutral',
    explanation: 'The customer did not complete this prompt.',
  },
  rejected: {
    label: 'Not sent',
    tone: 'neutral',
    explanation: 'M-Pesa refused this prompt; the customer never saw it.',
  },
  created: {
    label: 'Sending',
    tone: 'neutral',
    explanation: 'The prompt is being sent to the customer.',
  },
  pending: {
    label: 'Waiting for the customer',
    tone: 'neutral',
    explanation: 'The customer has not answered the prompt yet.',
  },
  checking: {
    label: 'Checking',
    tone: 'neutral',
    explanation: 'BizPlus is confirming this payment with M-Pesa.',
  },
};

const FALLBACK_STATUS = {
  label: 'Needs a check',
  tone: 'neutral' as PaymentToCheckTone,
  explanation: 'Check this payment against your M-Pesa statement.',
};

const FLAG_COPY: Record<string, { label: string; explanation: string }> = {
  possible_double_count: {
    label: 'Possible double count',
    explanation:
      'This M-Pesa code is also recorded as a separate payment. Check the order was not paid twice.',
  },
  receipt_mismatch: {
    label: 'Two different codes',
    explanation:
      'M-Pesa sent a different transaction code for this payment than the one recorded. Check which one is on your statement.',
  },
  paid_items_not_marked: {
    label: 'Items not marked paid',
    explanation:
      'The money is on the order, but the items it paid for could not be marked paid. Check them on the order.',
  },
};

/** Still in the owner's list: unresolved, or flagged. */
export function needsOwnerAttention(
  item: Pick<PaymentToCheck, 'bp_stk_status' | 'bp_flag'>,
): boolean {
  return UNRESOLVED_STATUSES.has(item.bp_stk_status) || Boolean(item.bp_flag);
}

export function describePaymentToCheck(
  item: PaymentToCheck,
): PaymentToCheckDescription {
  const status = STATUS_COPY[item.bp_stk_status] || FALLBACK_STATUS;
  const flag = item.bp_flag ? FLAG_COPY[item.bp_flag] : null;
  const flagLabel = item.bp_flag
    ? flag?.label || item.bp_flag.replace(/_/g, ' ')
    : null;
  const canResolve = UNRESOLVED_STATUSES.has(item.bp_stk_status);
  const bucket: PaymentToCheckBucket =
    item.bp_stk_status === 'unknown'
      ? 'unconfirmed'
      : item.bp_stk_status === 'needs_review'
        ? 'not_applied'
        : 'flagged';
  const canClearFlag = !canResolve && Boolean(item.bp_flag);
  return {
    bucket,
    statusLabel: status.label,
    // A settled flagged payment is shown by its flag, which is a warning
    // whatever the status says.
    tone: canClearFlag ? 'due' : status.tone,
    // A settled flagged payment is about its flag. An unresolved one gets the
    // owner's wording, never the till's "mark it paid or failed" message. A
    // settled one keeps the till's reason ("Customer cancelled the prompt").
    explanation:
      !canResolve && flag
        ? flag.explanation
        : canResolve
          ? status.explanation
          : item.bp_message || status.explanation,
    flagLabel,
    flagExplanation: flag?.explanation ?? null,
    canResolve,
    canClearFlag,
  };
}

export function matchesPaymentToCheckFilter(
  item: PaymentToCheck,
  filter: PaymentToCheckFilter,
): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'unconfirmed':
      return item.bp_stk_status === 'unknown';
    case 'not_applied':
      return item.bp_stk_status === 'needs_review';
    case 'flagged':
      return Boolean(item.bp_flag);
    default:
      return true;
  }
}

export function filterPaymentsToCheck(
  items: PaymentToCheck[],
  filter: PaymentToCheckFilter,
): PaymentToCheck[] {
  return filter === 'all'
    ? items
    : items.filter(item => matchesPaymentToCheckFilter(item, filter));
}

export type PaymentToCheckCounts = Record<PaymentToCheckFilter, number>;

export function countPaymentsToCheck(
  items: PaymentToCheck[],
): PaymentToCheckCounts {
  const counts: PaymentToCheckCounts = {
    all: items.length,
    unconfirmed: 0,
    not_applied: 0,
    flagged: 0,
  };
  for (const item of items) {
    if (item.bp_stk_status === 'unknown') counts.unconfirmed += 1;
    if (item.bp_stk_status === 'needs_review') counts.not_applied += 1;
    if (item.bp_flag) counts.flagged += 1;
  }
  return counts;
}

export type PaymentToCheckSection = { title: string; data: PaymentToCheck[] };

const createdAtMs = (item: PaymentToCheck): number => {
  const value = item.bp_created_at ? moment(item.bp_created_at) : null;
  return value && value.isValid() ? value.valueOf() : 0;
};

/** Newest first, grouped by the day the prompt was sent. */
export function groupPaymentsToCheckByDay(
  items: PaymentToCheck[],
  now: number = Date.now(),
): PaymentToCheckSection[] {
  const sorted = [...items].sort((a, b) => createdAtMs(b) - createdAtMs(a));
  const sections: PaymentToCheckSection[] = [];
  for (const item of sorted) {
    const title = notificationDayLabel(createdAtMs(item), now);
    const last = sections[sections.length - 1];
    if (last && last.title === title) last.data.push(item);
    else sections.push({ title, data: [item] });
  }
  return sections;
}

/** "11:48 PM": the day is the section heading. */
export function formatPaymentToCheckTime(item: PaymentToCheck): string {
  const value = item.bp_created_at ? moment(item.bp_created_at) : null;
  return value && value.isValid() ? value.format(DISPLAY_TIME_FORMAT) : '—';
}

export function formatMpesaAmount(amount: number | null | undefined): string {
  return `${MPESA_CURRENCY} ${formatMoney(Number(amount || 0))}`;
}

/** Who paid, as staff know them: the customer's name, else the phone. */
export function describePayer(item: PaymentToCheck): {
  name: string | null;
  phone: string | null;
  label: string;
} {
  const name = String(item.bp_payer?.name || '').trim() || null;
  const phone = String(item.bp_payer?.phone || '').trim() || null;
  return { name, phone, label: name || phone || 'M-Pesa customer' };
}

/** "Receipt BP827477264 · 3 items", "Receipt … · 2 of 4 items", or the M-Pesa code. */
export function describePaymentToCheckReference(item: PaymentToCheck): string {
  const parts: string[] = [];
  const order = item.bp_order;
  if (order?.receipt_ref) parts.push(`Receipt ${order.receipt_ref}`);
  if (order) {
    const count = order.item_count;
    const noun = count === 1 ? 'item' : 'items';
    parts.push(
      order.pays_item_count !== null && order.pays_item_count !== undefined
        ? `${order.pays_item_count} of ${count} ${noun}`
        : `${count} ${noun}`,
    );
  }
  if (item.bp_receipt) parts.push(`M-Pesa ${item.bp_receipt}`);
  return parts.join(' · ');
}

/** "2 × Ksh 250", or "0.5 kg × Ksh 1,300/kg" for a line sold by mass. */
export function describeOrderLineQuantity(
  line: PaymentToCheckOrderItem,
): string {
  if (line.mass !== null && line.mass > 0) {
    const unit = line.unit_of_mass || '';
    const rate = line.line_total / line.mass;
    const measure = `${formatNumberDecimal(line.mass)} ${unit}`.trim();
    if (!Number.isFinite(rate) || rate <= 0) return measure;
    return `${measure} × ${formatMpesaAmount(rate)}${unit ? `/${unit}` : ''}`;
  }
  if (line.quantity !== null && line.quantity > 0) {
    const unitPrice = line.line_total / line.quantity;
    return `${formatNumberDecimal(line.quantity)} × ${formatMpesaAmount(unitPrice)}`;
  }
  return '';
}

export function describePaidTo(
  paidTo: PaymentToCheckPaidTo | null | undefined,
): string | null {
  if (!paidTo?.number) return null;
  return `${paidTo.type === 'till' ? 'Till' : 'PayBill'} ${paidTo.number}`;
}

/** Same words and tones as the status pill on Order Details. */
export function describeOrderStatus(status: string | null | undefined): {
  label: string;
  tone: PaymentToCheckTone;
} {
  const value = String(status || '').toUpperCase();
  if (value === 'COMPLETED') return { label: 'Completed', tone: 'success' };
  if (value.includes('PARTIAL')) return { label: 'Partially paid', tone: 'due' };
  if (!value) return { label: 'Order', tone: 'neutral' };
  return {
    label: value.charAt(0) + value.slice(1).toLowerCase(),
    tone: 'info',
  };
}

/**
 * What recording this payment does to the order: "Paid in full" when it
 * covers what the order still owes, else the balance left. Null without an
 * order, or when the order is already complete.
 */
export function describeRecordOutcome(item: PaymentToCheck): string | null {
  const order = item.bp_order;
  if (!order || String(order.status || '').toUpperCase() === 'COMPLETED') {
    return null;
  }
  const amount = Number(item.bp_amount || 0);
  const due = Number(order.amount_due || 0);
  if (due <= 0 || amount >= due) return 'Paid in full';
  return `${formatMpesaAmount(due - amount)} balance`;
}

/** Replaces the prompt by id; drops it when it no longer needs the owner. */
export function upsertPaymentToCheck(
  items: PaymentToCheck[],
  updated: PaymentToCheck,
): PaymentToCheck[] {
  const id = String(updated.bp_stk_request_id);
  const rest = items.filter(item => String(item.bp_stk_request_id) !== id);
  if (!needsOwnerAttention(updated)) return rest;
  const index = items.findIndex(
    item => String(item.bp_stk_request_id) === id,
  );
  if (index === -1) return [updated, ...rest];
  return items.map(item =>
    String(item.bp_stk_request_id) === id ? updated : item,
  );
}

// ── The guide behind the header's info icon ─────────────────────────────

export type TextSegment = { text: string; bold: boolean };

/** "Tap **Record payment** now" → plain / bold / plain segments. */
export function splitBold(text: string): TextSegment[] {
  return String(text || '')
    .split(/\*\*(.+?)\*\*/g)
    .map((part, index) => ({ text: part, bold: index % 2 === 1 }))
    .filter(segment => segment.text.length > 0);
}

export interface PaymentsToCheckGuideFlag {
  key: string;
  label: string;
  text: string;
}

export interface PaymentsToCheckGuideSection {
  key: 'unknown' | 'needs_review' | 'flagged';
  label: string;
  tone: PaymentToCheckTone;
  /** Shown with the flag icon instead of a dot, as in the list. */
  flagged: boolean;
  body: string;
  flags?: PaymentsToCheckGuideFlag[];
  /** May hold **bold** action names. */
  whatToDo: string;
  steps?: string[];
}

export interface PaymentsToCheckGuide {
  title: string;
  intro: string[];
  sections: PaymentsToCheckGuideSection[];
  closing: string;
}

/**
 * What each kind of payment to check means and what the owner does about it.
 * Labels and tones come from the same tables as the list, so the guide always
 * names a status exactly as the rows show it.
 */
export const PAYMENTS_TO_CHECK_GUIDE: PaymentsToCheckGuide = {
  title: 'About payments to check',
  intro: [
    'BizPlus checks every M-Pesa payment with Safaricom before it marks an order paid. No fake SMS, no guesswork: money only counts once M-Pesa confirms it arrived.',
    'Almost every payment is confirmed in seconds. The few that need your eyes land here, so nothing slips through the cracks.',
  ],
  sections: [
    {
      key: 'unknown',
      label: STATUS_COPY.unknown.label,
      tone: STATUS_COPY.unknown.tone,
      flagged: false,
      body: 'The customer got the M-Pesa prompt, but Safaricom never told us how it ended. They may have paid, or they may have cancelled. BizPlus keeps checking, and if Safaricom answers, the payment sorts itself out and leaves this list.',
      whatToDo:
        'Look for it on your M-Pesa statement: same amount, same phone, around the same time.',
      steps: [
        'Found it? Tap **Record payment** and enter its M-Pesa transaction code. The money goes on the order straight away.',
        'Not there? Tap **Not paid**. The order stays open, ready to be paid another way.',
      ],
    },
    {
      key: 'needs_review',
      label: STATUS_COPY.needs_review.label,
      tone: STATUS_COPY.needs_review.tone,
      flagged: false,
      body: 'Good news: Safaricom confirmed the money is in your account. BizPlus just couldn’t add it to the order automatically.',
      whatToDo:
        'Tap **Record payment**. If M-Pesa sent the transaction code, it’s already filled in; otherwise copy it from your statement. Check it, confirm, and the order is updated at once.',
    },
    {
      key: 'flagged',
      label: 'Flagged',
      tone: 'due',
      flagged: true,
      body: 'BizPlus spotted something worth a second look:',
      flags: [
        {
          key: 'possible_double_count',
          label: FLAG_COPY.possible_double_count.label,
          text: 'The same M-Pesa code is also recorded as a separate payment. Open the order and make sure it wasn’t paid twice.',
        },
        {
          key: 'receipt_mismatch',
          label: FLAG_COPY.receipt_mismatch.label,
          text: 'M-Pesa sent a different transaction code from the one recorded. Check which one is on your statement.',
        },
        {
          key: 'paid_items_not_marked',
          label: FLAG_COPY.paid_items_not_marked.label,
          text: 'The money is on the order, but the items it paid for weren’t marked paid. Open the order and check them.',
        },
      ],
      whatToDo:
        'Once you’re happy, tap **Mark as checked**. This only clears the flag: your money and orders stay exactly as they are. If a flagged payment is also Not confirmed, settle that first.',
    },
  ],
  closing:
    'Settle them as they come, and your M-Pesa statement and your sales always match.',
};

// Same rule as the server's manual resolve (8 to 20 letters and digits).
const MPESA_CODE_RE = /^[A-Z0-9]{8,20}$/;

export function normalizeMpesaCode(text: string): string {
  return String(text || '')
    .trim()
    .toUpperCase();
}

export function isValidMpesaCode(text: string): boolean {
  return MPESA_CODE_RE.test(normalizeMpesaCode(text));
}
